// /coupons — admin de cupons de desconto (checkout).
// Lista + modal de criação. Cada cupom escolhe por quantos ciclos o desconto
// vale: só a 1ª cobrança (padrão), N cobranças, ou todas as renovações.

import * as Dialog from '@radix-ui/react-dialog';
import { Loader2, Plus, Power, Search, Ticket, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { formatBR } from '@control-center/shared';
import type { CreateCouponDto, QueryCouponsDto } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Input, Select, Textarea } from '../../components/clients/form-primitives';
import { PillButton } from '../../components/clients/PillButton';
import {
  useCouponRedemptions,
  useCoupons,
  useCreateCoupon,
  useDeleteCoupon,
  useUpdateCoupon,
} from '../../hooks/use-coupons';
import { usePlans } from '../../hooks/use-plans';
import type { CouponDerivedStatus, CouponRedemption, CouponView } from '../../lib/coupons-api';
import { extractErrorMessage } from '../../lib/errors';
import { useAuthStore } from '../../stores/useAuthStore';

const STATUS_LABEL: Record<CouponDerivedStatus, string> = {
  active: 'Ativo',
  inactive: 'Inativo',
  expired: 'Expirado',
  exhausted: 'Esgotado',
  scheduled: 'Agendado',
};
const STATUS_DOT: Record<CouponDerivedStatus, string> = {
  active: 'bg-good ring-good/15',
  inactive: 'bg-muted-2 ring-muted-3/15',
  expired: 'bg-bad ring-bad/15',
  exhausted: 'bg-warn ring-warn/15',
  scheduled: 'bg-muted-2 ring-muted-3/15',
};

function formatDiscount(c: CouponView): string {
  return c.discountType === 'percent'
    ? `${c.discountValue}%${c.maxDiscount != null ? ` (até R$ ${c.maxDiscount.toFixed(2)})` : ''}`
    : `R$ ${c.discountValue.toFixed(2)}`;
}
function formatUsage(c: CouponView): string {
  return c.usageLimit == null ? `${c.usageCount} / ∞` : `${c.usageCount} / ${c.usageLimit}`;
}
function formatValidity(c: CouponView): string {
  if (!c.validUntil) return 'Sem prazo';
  // `formatBR` (não formatDayBR): Coupon.validUntil é @db.Timestamptz — instante
  // real (gravado com `new Date(dto.valid_until)`), não dia puro. Com formatDayBR
  // um cupom válido até 31/07 21h BRT (= 01/08 00h UTC) apareceria como "até
  // 01/08" — um dia A MAIS. Os demais campos de data do CC (startDate,
  // nextBillingDate, dueDate…) são @db.Date e aí sim usam formatDayBR.
  return `até ${formatBR(c.validUntil)}`;
}

/**
 * Por quantos ciclos o desconto do cupom vale.
 *
 * O banco guarda MESES; a tela fala em decisões de negócio. A tradução mora
 * aqui pra que o operador não precise calcular "quantos meses são 20 anos".
 */
type DuracaoDesconto = 'primeira' | 'ciclos' | 'sempre';

/**
 * "Todas as renovações" em meses. O sistema conta em meses, não tem infinito.
 *
 * 240 = 20 anos. É vitalício na prática, e MELHOR que um infinito de verdade: a
 * data fica visível na assinatura do cliente, então algum dia alguém revisa em
 * vez de herdar um desconto sem explicação.
 */
const MESES_SEMPRE = 240;

/** Traduz a escolha da tela pro número que a API espera. */
export function duracaoEmMeses(opcao: DuracaoDesconto, ciclos: string): number | null {
  if (opcao === 'primeira') return null;
  if (opcao === 'sempre') return MESES_SEMPRE;
  const n = Number(ciclos);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
}

/** O que o cupom faz, em uma frase — mostrada abaixo do campo. */
export function explicarDuracao(opcao: DuracaoDesconto, ciclos: string): string {
  if (opcao === 'primeira') return 'Só a primeira fatura sai com desconto. A renovação volta ao preço cheio.';
  if (opcao === 'sempre') return 'Todas as renovações mantêm o desconto — o cliente nunca volta ao preço cheio.';
  const n = Number(ciclos);
  if (!Number.isFinite(n) || n <= 0) return 'Informe por quantas cobranças o desconto vale.';
  return `As ${n} primeiras cobranças saem com desconto. Da ${n + 1}ª em diante, preço cheio.`;
}

export function CouponsPage(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | QueryCouponsDto['status']>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [detalheId, setDetalheId] = useState<string | null>(null);

  const filters: QueryCouponsDto = useMemo(() => {
    const out: QueryCouponsDto = { limit: 50 };
    if (q.trim()) out.q = q.trim();
    if (statusFilter && statusFilter !== 'all') out.status = statusFilter;
    return out;
  }, [q, statusFilter]);

  const list = useCoupons(filters);
  const update = useUpdateCoupon();
  const remove = useDeleteCoupon();

  async function toggleActive(c: CouponView): Promise<void> {
    try {
      await update.mutateAsync({ id: c.id, payload: { is_active: !c.isActive } });
      toast.success(c.isActive ? 'Cupom desativado.' : 'Cupom reativado.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos atualizar o cupom.'));
    }
  }

  async function handleDelete(c: CouponView): Promise<void> {
    if (
      !window.confirm(
        `Desativar o cupom "${c.code}"?\n\nEle deixa de funcionar no checkout. O histórico de resgates é preservado.`,
      )
    ) {
      return;
    }
    try {
      await remove.mutateAsync(c.id);
      toast.success('Cupom removido.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos remover o cupom.'));
    }
  }

  const items = list.data?.items ?? [];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="m-0 text-[26px] font-bold tracking-[-0.025em] text-ink">Cupons</h1>
          <p className="mt-1 text-[13px] text-muted">
            Cupons de desconto aplicados no checkout. Cada cupom define se o desconto vale só na
            1ª cobrança ou também nas renovações.
          </p>
        </div>
        {isAdmin && (
          <PillButton
            solid
            icon={<Plus size={14} strokeWidth={2} />}
            onClick={() => setFormOpen(true)}
          >
            Novo cupom
          </PillButton>
        )}
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Field label="Buscar">
            {({ inputId }) => (
              <div className="relative">
                <Search
                  size={14}
                  strokeWidth={2}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-2"
                />
                <Input
                  id={inputId}
                  type="text"
                  placeholder="Código do cupom"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9"
                />
              </div>
            )}
          </Field>
        </div>
        <div className="min-w-[180px]">
          <Field label="Status">
            {({ inputId }) => (
              <Select
                id={inputId}
                value={statusFilter ?? 'all'}
                onChange={(e) =>
                  setStatusFilter(e.target.value as 'all' | QueryCouponsDto['status'])
                }
              >
                <option value="all">Todos</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
                <option value="expired">Expirados</option>
                <option value="exhausted">Esgotados</option>
              </Select>
            )}
          </Field>
        </div>
      </div>

      {list.isLoading ? (
        <div className="flex items-center justify-center rounded-[14px] border border-line bg-panel py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-2" />
        </div>
      ) : list.isError ? (
        <div className="rounded-[14px] border border-bad/30 bg-panel p-6 text-[13px] text-bad">
          {extractErrorMessage(list.error, 'Não conseguimos carregar os cupons.')}{' '}
          <button className="underline" onClick={() => void list.refetch()}>
            Tentar de novo
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-[14px] border border-line bg-panel py-16 text-center">
          <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-panel-3 text-muted-2">
            <Ticket size={20} strokeWidth={1.8} />
          </span>
          <p className="text-[14px] font-medium text-ink">Nenhum cupom ainda</p>
          <p className="mt-1 text-[13px] text-muted-2">
            Crie um cupom pra oferecer desconto no checkout.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
          <table className="w-full text-[13px]">
            <thead className="border-b border-line bg-panel-2">
              <tr className="text-left text-[11px] uppercase tracking-wider text-muted-2">
                <th className="px-4 py-3 font-medium">Código</th>
                <th className="px-4 py-3 font-medium">Desconto</th>
                <th className="px-4 py-3 font-medium">Escopo</th>
                <th className="px-4 py-3 font-medium tnum">Usos</th>
                <th className="px-4 py-3 font-medium">Validade</th>
                <th className="px-4 py-3 font-medium">Status</th>
                {isAdmin && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-line last:border-b-0 hover:bg-panel-2">
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2">
                      <span className="rounded-[6px] bg-panel-3 px-2 py-[3px] font-mono text-[12px] font-bold text-ink ring-1 ring-line">
                        {c.code}
                      </span>
                    </span>
                    {c.description && (
                      <p className="mt-1 max-w-[260px] truncate text-[11px] text-muted-2">
                        {c.description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-ink">{formatDiscount(c)}</td>
                  <td className="px-4 py-3 text-muted">
                    {c.appliesToAllPlans ? 'Todos os planos' : `${c.planIds.length} plano(s)`}
                  </td>
                  {/* O número de usos vira o caminho pro detalhe. É onde a
                      pessoa já olha quando quer saber "quem usou?" — pendurar
                      isso num botão separado seria pedir pra ela procurar. */}
                  <td className="px-4 py-3 tnum">
                    <button
                      type="button"
                      onClick={() => setDetalheId(c.id)}
                      title="Ver quem usou e quem fechou"
                      className="rounded-[6px] px-1.5 py-0.5 text-muted underline decoration-dotted underline-offset-4 hover:bg-panel-3 hover:text-ink"
                    >
                      {formatUsage(c)}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-muted">{formatValidity(c)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-[6px] text-[12px] text-muted">
                      <span
                        className={cn('h-[7px] w-[7px] rounded-full ring-2', STATUS_DOT[c.status])}
                      />
                      {STATUS_LABEL[c.status]}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          title={c.isActive ? 'Desativar' : 'Reativar'}
                          onClick={() => void toggleActive(c)}
                          className="rounded-[8px] p-2 text-muted-2 hover:bg-panel-3 hover:text-ink"
                        >
                          <Power size={15} strokeWidth={1.8} />
                        </button>
                        <button
                          title="Remover"
                          onClick={() => void handleDelete(c)}
                          className="rounded-[8px] p-2 text-muted-2 hover:bg-panel-3 hover:text-bad"
                        >
                          <Trash2 size={15} strokeWidth={1.8} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && <CouponFormModal open={formOpen} onOpenChange={setFormOpen} />}
      <RedemptionsPanel
        cupom={items.find((c) => c.id === detalheId) ?? null}
        onClose={() => setDetalheId(null)}
      />
    </div>
  );
}

// ============================================================================
// Modal de criação
// ============================================================================

interface CouponFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CouponFormModal({ open, onOpenChange }: CouponFormModalProps): JSX.Element {
  const create = useCreateCoupon();
  const plansList = usePlans({ limit: 100 });
  const plans = plansList.data?.items ?? [];

  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [discountType, setDiscountType] = useState<'percent' | 'fixed'>('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [maxDiscount, setMaxDiscount] = useState('');
  const [appliesToAll, setAppliesToAll] = useState(true);
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);
  const [usageLimit, setUsageLimit] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [minPurchase, setMinPurchase] = useState('');
  const [duracao, setDuracao] = useState<DuracaoDesconto>('primeira');
  const [duracaoCiclos, setDuracaoCiclos] = useState('');

  function reset(): void {
    setCode('');
    setDescription('');
    setDiscountType('percent');
    setDiscountValue('');
    setMaxDiscount('');
    setAppliesToAll(true);
    setSelectedPlanIds([]);
    setUsageLimit('');
    setValidFrom('');
    setValidUntil('');
    setMinPurchase('');
    setDuracao('primeira');
    setDuracaoCiclos('');
  }

  function handleOpenChange(next: boolean): void {
    if (!next) reset(); // reseta SEMPRE ao fechar (não só no submit)
    onOpenChange(next);
  }

  function togglePlan(id: string): void {
    setSelectedPlanIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (create.isPending) return; // anti double-click

    const valueNum = Number(discountValue);
    if (!code.trim() || code.trim().length < 4) {
      toast.error('Código deve ter ao menos 4 caracteres.');
      return;
    }
    if (!Number.isFinite(valueNum) || valueNum <= 0) {
      toast.error('Informe um valor de desconto válido.');
      return;
    }
    if (discountType === 'percent' && valueNum > 100) {
      toast.error('Percentual não pode passar de 100.');
      return;
    }
    if (!appliesToAll && selectedPlanIds.length === 0) {
      toast.error('Selecione ao menos 1 plano ou marque "todos os planos".');
      return;
    }
    // Escolheu "por N cobranças" e não disse quantas: sem esta trava o cupom
    // nasceria valendo só a 1ª — o oposto silencioso do que foi pedido.
    if (duracao === 'ciclos') {
      const ciclos = Number(duracaoCiclos);
      if (!Number.isInteger(ciclos) || ciclos < 1) {
        toast.error('Informe por quantas cobranças o desconto vale (número inteiro ≥ 1).');
        return;
      }
      if (ciclos > 600) {
        toast.error('Duração máxima é 600 cobranças. Para desconto permanente, use "Todas as renovações".');
        return;
      }
    }

    const payload: CreateCouponDto = {
      code: code.trim().toUpperCase(),
      description: description.trim() || null,
      discount_type: discountType,
      discount_value: valueNum,
      max_discount: discountType === 'percent' && maxDiscount.trim() ? Number(maxDiscount) : null,
      applies_to_all_plans: appliesToAll,
      plan_ids: appliesToAll ? [] : selectedPlanIds,
      usage_limit: usageLimit.trim() ? Number(usageLimit) : null,
      valid_from: validFrom ? new Date(validFrom).toISOString() : null,
      valid_until: validUntil ? new Date(validUntil).toISOString() : null,
      min_purchase: minPurchase.trim() ? Number(minPurchase) : null,
      discount_duration_months: duracaoEmMeses(duracao, duracaoCiclos),
    };

    try {
      await create.mutateAsync(payload);
      toast.success('Cupom criado.');
      handleOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos criar o cupom.'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90dvh] w-[min(560px,92vw)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[16px] border border-line bg-panel p-6 shadow-2xl">
          <div className="mb-5 flex items-center justify-between">
            <Dialog.Title className="text-[18px] font-bold text-ink">Novo cupom</Dialog.Title>
            <Dialog.Close className="rounded-[8px] p-1.5 text-muted-2 hover:bg-panel-3 hover:text-ink">
              <X size={16} />
            </Dialog.Close>
          </div>

          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <Field
              label="Código"
              required
              hint="4–50 caracteres · letras, números, hífen, underscore"
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="BEMVINDO20"
                  maxLength={50}
                  className="font-mono"
                />
              )}
            </Field>

            <Field label="Descrição (interna)">
              {({ inputId }) => (
                <Textarea
                  id={inputId}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Campanha de boas-vindas"
                  rows={2}
                  maxLength={500}
                />
              )}
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Tipo" required>
                {({ inputId }) => (
                  <Select
                    id={inputId}
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value as 'percent' | 'fixed')}
                  >
                    <option value="percent">Percentual (%)</option>
                    <option value="fixed">Valor fixo (R$)</option>
                  </Select>
                )}
              </Field>
              <Field label={discountType === 'percent' ? 'Desconto (%)' : 'Desconto (R$)'} required>
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    type="number"
                    min="0"
                    step={discountType === 'percent' ? '1' : '0.01'}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder={discountType === 'percent' ? '20' : '30,00'}
                    tabular
                  />
                )}
              </Field>
            </div>

            {discountType === 'percent' && (
              <Field
                label="Teto do desconto (R$)"
                hint="Opcional — limita o valor abatido no percentual"
              >
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    type="number"
                    min="0"
                    step="0.01"
                    value={maxDiscount}
                    onChange={(e) => setMaxDiscount(e.target.value)}
                    placeholder="ex: 50,00"
                    tabular
                  />
                )}
              </Field>
            )}

            <Field label="Escopo">
              {() => (
                <div className="space-y-2">
                  <label className="flex cursor-pointer items-center gap-2 text-[13px] text-ink">
                    <input
                      type="checkbox"
                      checked={appliesToAll}
                      onChange={(e) => setAppliesToAll(e.target.checked)}
                      className="h-4 w-4 rounded border-line accent-teal-600"
                    />
                    Vale pra todos os planos
                  </label>
                  {!appliesToAll && (
                    <div className="max-h-[160px] space-y-1 overflow-y-auto rounded-[10px] border border-line bg-panel-2 p-2">
                      {plans.length === 0 ? (
                        <p className="px-1 py-2 text-[12px] text-muted-2">Carregando planos…</p>
                      ) : (
                        plans.map((p) => (
                          <label
                            key={p.id}
                            className="flex cursor-pointer items-center gap-2 rounded-[8px] px-2 py-1.5 text-[13px] text-ink hover:bg-panel-3"
                          >
                            <input
                              type="checkbox"
                              checked={selectedPlanIds.includes(p.id)}
                              onChange={() => togglePlan(p.id)}
                              className="h-4 w-4 rounded border-line accent-teal-600"
                            />
                            {p.name}
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Limite de usos" hint="Vazio = ilimitado">
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    type="number"
                    min="1"
                    step="1"
                    value={usageLimit}
                    onChange={(e) => setUsageLimit(e.target.value)}
                    placeholder="∞"
                    tabular
                  />
                )}
              </Field>
              <Field label="Compra mínima (R$)" hint="Opcional">
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    type="number"
                    min="0"
                    step="0.01"
                    value={minPurchase}
                    onChange={(e) => setMinPurchase(e.target.value)}
                    placeholder="ex: 100,00"
                    tabular
                  />
                )}
              </Field>
            </div>

            {/* Duração do desconto — o campo que responde "e na renovação?".
                Antes, todo cupom valia só a 1ª cobrança e não havia como mudar
                isso pela tela. Fica aqui, logo após limite de uso e compra
                mínima, porque é da mesma família: as regras de quando o cupom
                vale. */}
            <Field
              label="O desconto vale por"
              hint="Renovações seguintes: preço cheio ou desconto mantido?"
            >
              {({ inputId }) => (
                <div className="space-y-2">
                  <Select
                    id={inputId}
                    value={duracao}
                    onChange={(e) => setDuracao(e.target.value as DuracaoDesconto)}
                  >
                    <option value="primeira">Só a primeira cobrança</option>
                    <option value="ciclos">Um número de cobranças</option>
                    <option value="sempre">Todas as renovações</option>
                  </Select>

                  {duracao === 'ciclos' && (
                    <Input
                      type="number"
                      min="1"
                      max="600"
                      step="1"
                      value={duracaoCiclos}
                      onChange={(e) => setDuracaoCiclos(e.target.value)}
                      placeholder="ex: 3 cobranças"
                      aria-label="Número de cobranças com desconto"
                      tabular
                    />
                  )}

                  <p className="text-[12px] leading-relaxed text-muted">
                    {explicarDuracao(duracao, duracaoCiclos)}
                  </p>
                </div>
              )}
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Válido a partir de" hint="Opcional">
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    type="datetime-local"
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Válido até" hint="Opcional">
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    type="datetime-local"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                  />
                )}
              </Field>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="rounded-[10px] border border-line px-4 py-2 text-[13px] font-medium text-muted hover:bg-panel-2"
                >
                  Cancelar
                </button>
              </Dialog.Close>
              <button
                type="submit"
                disabled={create.isPending}
                className={cn(
                  'flex items-center gap-2 rounded-[10px] px-4 py-2 text-[13px] font-bold text-white transition-colors',
                  create.isPending
                    ? 'cursor-not-allowed bg-teal-600/50'
                    : 'bg-teal-600 hover:bg-teal-700',
                )}
              >
                {create.isPending && <Loader2 size={14} className="animate-spin" />}
                Criar cupom
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// =============================================================================
// Painel de resgates — quem usou o cupom, quem fechou e quanto custou
//
// POR QUE EXISTE: o banco já gravava cada uso com cliente, status e valores, e
// nada lia. A tabela mostrava "Usos: 3" — um número solto. Numa campanha de
// influencer isso não responde nada: não dá pra saber quem entrou, quantos
// desistiram no meio, nem quanto o desconto custou.
// =============================================================================

const REDEMPTION_LABEL: Record<CouponRedemption['status'], string> = {
  confirmed: 'Pagou',
  reserved: 'Não pagou ainda',
  released: 'Abandonou',
};
const REDEMPTION_DOT: Record<CouponRedemption['status'], string> = {
  confirmed: 'bg-good ring-good/15',
  reserved: 'bg-warn ring-warn/15',
  released: 'bg-muted-2 ring-muted-3/15',
};

function brl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function RedemptionsPanel({
  cupom,
  onClose,
}: {
  cupom: CouponView | null;
  onClose: () => void;
}): JSX.Element {
  const q = useCouponRedemptions(cupom?.id ?? null);
  const s = q.data?.summary;

  return (
    <Dialog.Root open={cupom !== null} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90dvh] w-[min(760px,94vw)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[16px] border border-line bg-panel p-6 shadow-2xl">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="flex items-center gap-2 text-[18px] font-bold text-ink">
                <span className="rounded-[6px] bg-panel-3 px-2 py-[3px] font-mono text-[13px] ring-1 ring-line">
                  {cupom?.code}
                </span>
                <span className="text-[15px] font-medium text-muted">quem usou</span>
              </Dialog.Title>
              {cupom?.description && (
                <p className="mt-1 text-[12px] text-muted-2">{cupom.description}</p>
              )}
            </div>
            <Dialog.Close className="rounded-[8px] p-1.5 text-muted-2 hover:bg-panel-3 hover:text-ink">
              <X size={16} />
            </Dialog.Close>
          </div>

          {q.isLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-muted-2" size={24} />
            </div>
          )}

          {q.isError && (
            <p className="rounded-[12px] border border-bad/30 bg-bad/5 p-4 text-[13px] text-ink-2">
              {extractErrorMessage(q.error, 'Não conseguimos carregar os usos deste cupom.')}
            </p>
          )}

          {s && (
            <>
              {/* Conversão em destaque: numa campanha, "quantos dos que tentaram
                  fecharam" é A pergunta; o resto é detalhe dela. */}
              <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Metric
                  label="Converteram"
                  value={s.conversionRate === null ? '—' : `${Math.round(s.conversionRate * 100)}%`}
                  hint={`${s.confirmed} de ${s.attempts}`}
                  destaque
                />
                <Metric label="Receita" value={brl(s.revenue)} hint="já com desconto" />
                <Metric
                  label="Desconto dado"
                  value={brl(s.discountGiven)}
                  hint="só de quem pagou"
                />
                <Metric
                  label="Em aberto"
                  value={String(s.pending)}
                  hint={s.abandoned > 0 ? `${s.abandoned} desistiram` : 'ninguém desistiu'}
                />
              </div>

              {q.data && q.data.items.length === 0 ? (
                <div className="rounded-[12px] border border-dashed border-line py-12 text-center">
                  <Ticket size={26} strokeWidth={1.5} className="mx-auto text-muted-3" />
                  <p className="mt-3 text-[13px] text-muted">Ninguém usou este cupom ainda.</p>
                  <p className="mt-1 text-[12px] text-muted-2">
                    Assim que alguém aplicar no checkout, aparece aqui — mesmo antes de pagar.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-[12px] border border-line">
                  <table className="w-full text-left text-[13px]">
                    <thead className="border-b border-line bg-panel-2 text-[11px] uppercase tracking-wider text-muted-2">
                      <tr>
                        <th className="px-4 py-2.5 font-medium">Cliente</th>
                        <th className="px-4 py-2.5 font-medium">Situação</th>
                        {/* "Valor pago", não "Pagou": a coluna de situação já
                            usa "Pagou" como status. Duas coisas diferentes com
                            o mesmo rótulo na mesma tabela é convite a erro. */}
                        <th className="px-4 py-2.5 text-right font-medium">Valor pago</th>
                        <th className="px-4 py-2.5 text-right font-medium">Desconto</th>
                        <th className="px-4 py-2.5 font-medium">Quando</th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.data?.items.map((r) => (
                        <tr key={r.id} className="border-b border-line last:border-b-0">
                          <td className="px-4 py-3">
                            {/* Cliente anonimizado (LGPD) continua contando na
                                métrica — some o nome, não a linha. */}
                            <div className="font-medium text-ink">
                              {r.clientName ?? 'Cliente removido'}
                            </div>
                            {r.clientEmail && (
                              <div className="text-[11px] text-muted-2">{r.clientEmail}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-[6px] text-[12px] text-muted">
                              <span
                                className={cn(
                                  'h-[7px] w-[7px] rounded-full ring-2',
                                  REDEMPTION_DOT[r.status],
                                )}
                              />
                              {REDEMPTION_LABEL[r.status]}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="tnum font-medium text-ink">{brl(r.finalPrice)}</span>
                            <div className="tnum text-[11px] text-muted-3 line-through">
                              {brl(r.originalPrice)}
                            </div>
                          </td>
                          <td className="tnum px-4 py-3 text-right text-muted">
                            −{brl(r.discountApplied)}
                          </td>
                          <td className="px-4 py-3 text-[12px] text-muted-2">
                            {formatBR(r.confirmedAt ?? r.usedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Metric({
  label,
  value,
  hint,
  destaque,
}: {
  label: string;
  value: string;
  hint: string;
  destaque?: boolean;
}): JSX.Element {
  return (
    <div
      className={cn(
        'rounded-[12px] border p-3',
        destaque ? 'border-ink/20 bg-panel-2' : 'border-line',
      )}
    >
      <div className="text-[11px] uppercase tracking-wider text-muted-2">{label}</div>
      <div className={cn('tnum mt-1 font-bold text-ink', destaque ? 'text-[24px]' : 'text-[18px]')}>
        {value}
      </div>
      <div className="mt-0.5 text-[11px] text-muted-3">{hint}</div>
    </div>
  );
}
